import React, { useState, useContext, useEffect, useRef } from "react";
import { TranslationContext } from "./TranslationContext";
import {
  SUPPORTED_LANGUAGES,
  normalizeLanguageCode,
} from "./translationHelpers";
import { Tooltip } from "antd";
import { useBadgePosition } from "@src/hooks/useBadgePosition";
import Spinner from "../spinner/Spinner";
import { useUser } from "@src/hooks/useUser";
import { IconTranslation } from "@src/constants";
import cssStyles from "./TranslationBadge.module.scss";
import { sortByAlphabetic } from "@src/utils/helpers";
import { logger } from "../../utils/logger";
// Use the same value as $HeaderHeight from _constants.scss
const HEADER_HEIGHT = 80;

// Static counter to ensure only one modal badge is rendered
let modalBadgeRendered = false;

// Keyframes for the flashing effect
const flashingAnimation = `
  @keyframes flash {
    0% { opacity: 0.8; }
    50% { opacity: 1; }
    100% { opacity: 0.8; }
  }
`;

type TranslationBadgeProps = {
  type?: "presentation" | "navbar" | "modal" | "preview";
};

const TranslationBadge: React.FC<TranslationBadgeProps> = ({ type }) => {
  const context = useContext(TranslationContext);

  const {
    isTranslated = false,
    isTranslating = false,
    currentLanguage = "EN",
    pendingLanguageChange = null,
    changeLanguage = () => logger.error("TranslationProvider not found"),
    stopTranslation = () => logger.error("TranslationProvider not found"),
    clearTranslationCache = () => logger.error("Translation Cache cleared"),
  } = context || {};

  // Always call hooks unconditionally
  const hookBadgePosition = useBadgePosition();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredLanguage, setHoveredLanguage] = useState<string | null>(null);

  // Get user data to check country
  const [user] = useUser();

  // Check if user is from Australia
  const isAUuser = user?.id?.toLowerCase().startsWith("au");

  // Add the flashing animation to the document
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.innerHTML = flashingAnimation;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Close the dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".translation-badge")) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, []);

  // Use a ref to track if this component instance has rendered a modal badge
  const hasRenderedModalBadge = useRef(false);

  // When the component mounts, check if it's a modal badge and update the static counter
  useEffect(() => {
    if (type === "modal") {
      if (modalBadgeRendered && !hasRenderedModalBadge.current) {
        // If a modal badge is already rendered and this instance hasn't rendered one yet,
        // mark this instance as not needing to render
        hasRenderedModalBadge.current = false;
      } else if (!modalBadgeRendered) {
        // If no modal badge is rendered yet, mark this instance as the one that will render
        modalBadgeRendered = true;
        hasRenderedModalBadge.current = true;
      }
    }

    // Clean up when the component unmounts
    return () => {
      if (type === "modal" && hasRenderedModalBadge.current) {
        // If this instance rendered a modal badge, reset the static counter
        modalBadgeRendered = false;
        hasRenderedModalBadge.current = false;
      }
    };
  }, [type]);

  // Prevent duplicate badges when both modal and presentation are active
  // If we're in a modal and presentation is active, don't render the modal badge
  if (type === "modal" && hookBadgePosition === "presentation") {
    logger.info(
      "[TranslationBadge] Skipping modal badge because presentation is active",
    );
    return null;
  }

  // If we're in presentation mode but the hook says we're not, don't render
  if (type === "preview" && hookBadgePosition !== "preview") {
    return null;
  }

  // If we're in presentation mode but the hook says we're not, don't render
  if (type === "presentation" && hookBadgePosition !== "presentation") {
    return null;
  }

  // If this is a modal badge but not the one that should be rendered, don't render it
  if (type === "modal" && !hasRenderedModalBadge.current) {
    logger.info(
      "[TranslationBadge] Skipping modal badge because another one is already rendered",
    );
    return null;
  }

  // If user is from Australia, don't render the badge
  // if (isAUuser) {
  //   return null;
  // }

  // Then use the type prop if provided, otherwise use the hook result
  const badgePosition = type || hookBadgePosition;

  // Filter languages based on search term
  const filteredLanguages = SUPPORTED_LANGUAGES.filter((lang) =>
    lang.name.toLowerCase().includes(searchTerm.toLowerCase()),
  ).sort(sortByAlphabetic);

  // Helper function to get the tooltip text
  const getTooltipText = () => {
    if (isTranslating || pendingLanguageChange) {
      return "Click to cancel the current translation process";
    } else if (
      isTranslated &&
      normalizeLanguageCode(currentLanguage) !== "EN"
    ) {
      return `Currently viewing content in ${SUPPORTED_LANGUAGES.find((lang) => lang.code === currentLanguage)?.name || currentLanguage}. Click to change language or disable translation.`;
    } else {
      return "Translate Page";
    }
  };

  // Helper function to handle badge click
  const handleBadgeClick = () => {
    // If currently translating, cancel the translation and revert to English
    if (
      isTranslating ||
      pendingLanguageChange ||
      (context && context.nodesBeingTranslated > 0)
    ) {
      stopTranslation();
    } else {
      // Otherwise, open the dropdown
      setIsDropdownOpen(true);
    }
  };

  // Helper function to handle disable translation click
  const handleDisableTranslation = () => {
    stopTranslation();
    clearTranslationCache();
    setIsDropdownOpen(false);
  };

  // Helper function to handle language selection
  const handleLanguageSelect = (lang: string) => {
    if (lang === "EN" && isTranslated) {
      stopTranslation();
    }

    changeLanguage(lang);
    setIsDropdownOpen(false);
  };

  // Render the language dropdown
  const renderLanguageDropdown = () => {
    if (!isDropdownOpen) return null;

    // Position the dropdown based on badge position
    const dropdownStyle = {
      position: "absolute" as const,
      top:
        badgePosition === "presentation"
          ? "50px"
          : badgePosition === "navbar"
            ? "90px"
            : badgePosition === "modal"
              ? `${HEADER_HEIGHT}px`
              : "45px",
      right:
        badgePosition === "presentation"
          ? "-40px"
          : badgePosition === "navbar"
            ? "95px"
            : "0",
      width: "210px",
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
      padding: "10px",
      zIndex: 9999,
      border: "1px solid #f0f0f0",
    };

    const isTranslationActive =
      isTranslating ||
      (context && context.nodesBeingTranslated > 0) ||
      isTranslated;

    return (
      <div style={dropdownStyle} onClick={(e) => e.stopPropagation()}>
        {/* Show the Disable Translation button when translation is active */}
        {isTranslationActive && (
          <>
            <div
              style={{
                padding: "8px 15px",
                cursor: "pointer",
                borderRadius: "4px",
                fontWeight: "bold",
                color: "#d9534f",
                marginBottom: "5px",
                fontFamily: "Montserrat, sans-serif",
                backgroundColor:
                  hoveredLanguage === "OFF" ? "#FFF0E6" : "transparent",
              }}
              onClick={handleDisableTranslation}
              onMouseEnter={() => setHoveredLanguage("OFF")}
              onMouseLeave={() => setHoveredLanguage(null)}
            >
              Disable Translation
            </div>

            <div
              style={{
                height: "1px",
                backgroundColor: "#f0f0f0",
                margin: "5px 0 10px 0",
              }}
            />
          </>
        )}

        <input
          type="text"
          placeholder="Search languages..."
          style={{
            width: "100%",
            padding: "8px",
            marginBottom: "10px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            boxSizing: "border-box" as const,
            fontFamily: "Montserrat, sans-serif",
            fontSize: "14px",
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />

        <div style={{ maxHeight: "300px", overflowY: "auto" as const }}>
          {/* English option at the top */}
          <div
            style={{
              padding: "8px 15px",
              cursor: "pointer",
              borderRadius: "4px",
              fontFamily: "Montserrat, sans-serif",
              fontSize: "16px",
              fontWeight: 500,
              backgroundColor:
                currentLanguage === "EN"
                  ? "#FFD8B8"
                  : hoveredLanguage === "EN"
                    ? "#FFF0E6"
                    : "transparent",
            }}
            onClick={() => handleLanguageSelect("EN")}
            onMouseEnter={() => setHoveredLanguage("EN")}
            onMouseLeave={() => setHoveredLanguage(null)}
          >
            English
          </div>

          <div
            style={{
              height: "1px",
              backgroundColor: "#f0f0f0",
              margin: "5px 0",
            }}
          />

          {/* Other languages */}
          {filteredLanguages
            .filter((lang) => lang.code !== "EN")
            .map((lang) => (
              <div
                key={lang.code}
                style={{
                  padding: "8px 15px",
                  cursor: "pointer",
                  borderRadius: "4px",
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: "16px",
                  fontWeight: 500,
                  backgroundColor:
                    currentLanguage === lang.code
                      ? "#FFD8B8"
                      : hoveredLanguage === lang.code
                        ? "#FFF0E6"
                        : "transparent",
                }}
                onClick={() => handleLanguageSelect(lang.code)}
                onMouseEnter={() => setHoveredLanguage(lang.code)}
                onMouseLeave={() => setHoveredLanguage(null)}
              >
                {lang.name}
              </div>
            ))}
        </div>
      </div>
    );
  };

  // Render the badge icon
  const renderBadgeIcon = () => {
    // In presentation mode when translation is active, use white for the icon
    const isPresentationActive =
      badgePosition === "presentation" &&
      isTranslated &&
      normalizeLanguageCode(currentLanguage) !== "EN";

    if (isTranslating || (context && context.nodesBeingTranslated > 0)) {
      return (
        <div
          style={{
            filter: isPresentationActive
              ? "brightness(0) invert(1)" // White spinner for presentation mode when active
              : "hue-rotate(-10deg) saturate(2) brightness(1.2)",
          }}
        >
          <Spinner
            width={
              badgePosition === "presentation" || badgePosition === "preview"
                ? 30
                : 45
            }
            height={
              badgePosition === "presentation" || badgePosition === "preview"
                ? 30
                : 45
            }
          />
        </div>
      );
    } else {
      return (
        <lord-icon
          src={IconTranslation}
          trigger={
            isTranslated && normalizeLanguageCode(currentLanguage) !== "EN"
              ? "loop"
              : "loop-on-hover"
          }
          colors={`primary:${
            isPresentationActive
              ? "#FFFFFF" // White icon for presentation mode when active
              : isTranslated && normalizeLanguageCode(currentLanguage) !== "EN"
                ? "#ffb073"
                : "#274649"
          },secondary:${
            isPresentationActive
              ? "#FFFFFF" // White icon for presentation mode when active
              : isTranslated && normalizeLanguageCode(currentLanguage) !== "EN"
                ? "#ffb073"
                : "#274649"
          }`}
          style={{
            width:
              badgePosition === "presentation" || badgePosition === "preview"
                ? "30px"
                : "45px",
            height:
              badgePosition === "presentation" || badgePosition === "preview"
                ? "30px"
                : "45px",
          }}
        ></lord-icon>
      );
    }
  };

  // Render the badge based on its position
  if (badgePosition === "modal") {
    // Modal badge (circular)
    return (
      <div className="translation-badge">
        <Tooltip title={getTooltipText()} placement="left">
          <div
            className={cssStyles.modalBadge}
            onClick={handleBadgeClick}
            style={{
              animation:
                isTranslated && normalizeLanguageCode(currentLanguage) !== "EN"
                  ? "flash 2s infinite"
                  : "none",
            }}
          >
            {/* Custom icon rendering for modal badge */}
            {isTranslating || (context && context.nodesBeingTranslated > 0) ? (
              // Use orange for active translation/loading
              <div
                style={{
                  filter: "hue-rotate(-10deg) saturate(2) brightness(1.2)",
                }}
              >
                <Spinner width={50} height={50} />
              </div>
            ) : isTranslated &&
              normalizeLanguageCode(currentLanguage) !== "EN" ? (
              // Use orange for translated state
              <lord-icon
                src={IconTranslation}
                trigger="loop"
                colors="primary:#ffb073,secondary:#ffb073"
                style={{ width: "50px", height: "50px" }}
              ></lord-icon>
            ) : (
              // Use white for default state
              <lord-icon
                src={IconTranslation}
                trigger="loop-on-hover"
                colors="primary:#FFFFFF,secondary:#FFFFFF"
                style={{ width: "50px", height: "50px" }}
              ></lord-icon>
            )}
          </div>
        </Tooltip>
        {renderLanguageDropdown()}
      </div>
    );
  } else if (badgePosition === "presentation" || badgePosition === "preview") {
    // Presentation mode badge (rectangular with text) - positioned in the timeline
    return (
      <div
        className="translation-badge"
        style={{
          position: "relative",
          display: "inline-block",
          marginRight: "10px",
        }}
      >
        {isDropdownOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9998,
              cursor: "pointer",
            }}
            onClick={() => setIsDropdownOpen(false)}
          />
        )}

        <Tooltip title={getTooltipText()} placement="left">
          <div
            className={cssStyles.translateRectButton}
            onClick={handleBadgeClick}
            data-translated={isTranslated ? "true" : "false"}
            data-translating={isTranslating ? "true" : "false"}
            style={{
              position: "relative",
              float: "none",
              display: "flex",
              marginRight: "0", // Remove margin for presentation mode
            }}
          >
            <div className={cssStyles.translateIconWrapper}>
              {renderBadgeIcon()}
            </div>
            <span
              className={cssStyles.translateText}
              style={{ color: isTranslated ? "#FFFFFF" : "var(--color-brown)" }}
            >
              Translate
            </span>
          </div>
        </Tooltip>
        {renderLanguageDropdown()}
      </div>
    );
  } else {
    // Header badge (circular)
    return (
      <div className="translation-badge">
        <Tooltip title={getTooltipText()} placement="left">
          <div
            style={{
              position: "relative",
              width: "45px",
              height: "45px",
              borderRadius: "50%",
              backgroundColor: "transparent", // Always transparent background
              color: "var(--color-green, #274649)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
              fontSize: "20px",
              margin: "0 2px",
              padding: "0",
              border: "none",
              animation:
                isTranslated && normalizeLanguageCode(currentLanguage) !== "EN"
                  ? "flash 2s infinite"
                  : "none",
            }}
            onClick={handleBadgeClick}
          >
            {renderBadgeIcon()}
          </div>
        </Tooltip>
        {renderLanguageDropdown()}
      </div>
    );
  }
};

export default TranslationBadge;
